<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import accountTemplatesAPI from 'dashboard/api/accountTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import NewTemplateDialog from './NewTemplateDialog.vue';

const { t } = useI18n();
const templates = ref([]);
const loading = ref(true);
const showNew = ref(false);
let timer = null;

const itemsCount = template =>
  Object.values(template.items_summary || {}).reduce((a, b) => a + b, 0);
const summaryLine = template =>
  [
    t('REVENDA.TEMPLATES.ITEMS_COUNT', { count: itemsCount(template) }),
    t('REVENDA.TEMPLATES.APPLIED_COUNT', {
      count: template.applications_count,
    }),
  ].join(' · ');

const statusLine = template => {
  if (template.status === 'building') return t('REVENDA.TEMPLATES.BUILDING');
  if (template.status === 'failed') return t('REVENDA.TEMPLATES.FAILED');
  return summaryLine(template);
};

const load = async () => {
  try {
    const { data } = await accountTemplatesAPI.get();
    templates.value = data.templates || [];
  } finally {
    loading.value = false;
  }
  clearTimeout(timer);
  if (templates.value.some(item => item.status === 'building'))
    timer = setTimeout(load, 3000);
};

const remove = async template => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('REVENDA.TEMPLATES.DELETE_CONFIRM'))) return;
  try {
    await accountTemplatesAPI.delete(template.id);
    await load();
  } catch (error) {
    useAlert(t('REVENDA.TEMPLATES.FAILED'));
  }
};

const onCreated = () => {
  showNew.value = false;
  load();
};

onMounted(load);
onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('REVENDA.TEMPLATES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('REVENDA.TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="t('REVENDA.TEMPLATES.NEW')"
        icon="i-lucide-plus"
        @click="showNew = true"
      />
    </div>
    <p v-if="!loading && !templates.length" class="text-sm text-n-slate-11">
      {{ t('REVENDA.TEMPLATES.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="template in templates"
        :key="template.id"
        class="flex items-center justify-between p-3 border rounded-lg border-n-weak"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">{{ template.name }}</p>
          <p
            class="text-xs"
            :class="
              template.status === 'failed'
                ? 'text-n-ruby-11'
                : 'text-n-slate-11'
            "
          >
            {{ statusLine(template) }}
          </p>
        </div>
        <Button
          v-if="template.visibility === 'private'"
          variant="ghost"
          color="ruby"
          size="sm"
          :label="t('REVENDA.TEMPLATES.DELETE')"
          @click="remove(template)"
        />
      </li>
    </ul>
    <NewTemplateDialog
      v-if="showNew"
      @close="showNew = false"
      @created="onCreated"
    />
  </div>
</template>
